import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, RotateCcw, Shield, UserCog } from "lucide-react";

/**
 * Per-User Permission Grid.
 *
 * Grid renders modules × actions cells. Each cell is either:
 *   • Inherited ON  (from role)     — grey tick, editable to force-DENY
 *   • Explicit ALLOW               — green tick with `A` badge
 *   • Explicit DENY                — red x with `D` badge
 *   • Inherited OFF                — blank, editable to force-ALLOW
 *
 * Clicking cycles through: role-inherit → allow → deny → clear override.
 */
const CYCLE = ["inherit", "allow", "deny"];

export default function PermissionGrid() {
  const [users, setUsers]     = useState([]);
  const [meta, setMeta]       = useState({ modules: [], actions: [], grid_map: {} });
  const [selected, setSelected] = useState("");
  const [state, setState]     = useState(null);       // { role_perms, override, effective, grid, user }
  const [pending, setPending] = useState({});          // { "module.action": "allow"|"deny"|"inherit" }
  const [busy, setBusy]       = useState(false);

  // Load users + grid meta
  useEffect(() => {
    (async () => {
      const [u, g] = await Promise.all([api.get("/users"), api.get("/permissions/grid")]);
      setUsers(u.data || []);
      setMeta(g.data);
      if ((u.data || []).length) setSelected(u.data[0].id);
    })();
  }, []);

  const loadUser = async (uid) => {
    if (!uid) return;
    const r = await api.get(`/permissions/users/${uid}`);
    setState(r.data);
    setPending({});
  };
  useEffect(() => { if (selected) loadUser(selected); }, [selected]);

  const rolePerms = useMemo(() => new Set(state?.role_perms || []), [state]);
  const allowSet  = useMemo(() => new Set(state?.override?.allow || []), [state]);
  const denySet   = useMemo(() => new Set(state?.override?.deny  || []), [state]);

  // Cell state after applying pending
  const cellStatus = (module, action) => {
    const key = `${module}.${action}`;
    const gp  = meta.grid_map[key] || [];
    if (pending[key]) return pending[key];   // pending overrides all
    // Any granular in allow? → allow
    if (gp.some(p => allowSet.has(p))) return "allow";
    // Any granular in deny? → deny
    if (gp.some(p => denySet.has(p))) return "deny";
    // Any granular in role? → inherit ON
    if (gp.some(p => rolePerms.has(p))) return "inherit_on";
    return "inherit_off";
  };

  const cycleCell = (module, action) => {
    const key = `${module}.${action}`;
    const cur = cellStatus(module, action);
    let next;
    if (cur === "inherit_off" || cur === "inherit") next = "allow";
    else if (cur === "inherit_on") next = "deny";
    else if (cur === "allow") next = "deny";
    else if (cur === "deny") next = "inherit";
    setPending({ ...pending, [key]: next });
  };

  const save = async () => {
    if (!state) return;
    setBusy(true);
    try {
      // Rebuild allow/deny by walking every cell (including pending overrides).
      const nextAllow = new Set();
      const nextDeny  = new Set();
      for (const mod of meta.modules) {
        for (const act of meta.actions) {
          const key = `${mod.key}.${act}`;
          const cur = cellStatus(mod.key, act);
          const gp  = meta.grid_map[key] || [];
          if (cur === "allow")      gp.forEach(p => nextAllow.add(p));
          else if (cur === "deny")  gp.forEach(p => nextDeny.add(p));
        }
      }
      await api.put(`/permissions/users/${selected}`, {
        allow: Array.from(nextAllow), deny: Array.from(nextDeny),
      });
      toast.success("Permissions saved");
      await loadUser(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!confirm("Remove ALL per-user overrides and revert to role defaults?")) return;
    setBusy(true);
    try {
      await api.delete(`/permissions/users/${selected}`);
      toast.success("Overrides cleared — user now inherits role defaults");
      await loadUser(selected);
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const CELL_CLASS = {
    inherit_on:  "bg-slate-100 text-slate-600 border-slate-200",
    inherit_off: "bg-white text-slate-300 border-slate-200",
    allow:       "bg-emerald-500/15 text-emerald-700 border-emerald-500",
    deny:        "bg-red-500/15 text-red-700 border-red-500",
  };
  const CELL_ICON = {
    inherit_on:  "✓",
    inherit_off: "·",
    allow:       "A",
    deny:        "D",
  };
  const dirty = Object.keys(pending).length > 0;

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="permission-grid">
      <div className="flex items-center justify-between border-b pb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
            <UserCog className="w-3.5 h-3.5" /> Per-User Permission Grid
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Override individual cells for one user. Cells inherit from the user's role by default; explicit <b className="text-emerald-600">Allow</b> or <b className="text-red-600">Deny</b> takes precedence.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-72 rounded-sm h-9" data-testid="perm-user-picker">
              <SelectValue placeholder="Pick a user…" />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id} data-testid={`perm-user-opt-${u.id}`}>
                  {u.full_name || u.email} · <span className="font-mono text-[10px]">{u.role_code || "no role"}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!state ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          <Shield className="w-10 h-10 mx-auto opacity-30" />
          <div className="mt-2">Pick a user to edit their permissions.</div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between p-3 rounded-sm bg-slate-50 border">
            <div className="text-sm">
              <div className="font-semibold">{state.user.full_name || state.user.email}</div>
              <div className="text-[11px] font-mono text-muted-foreground">
                {state.user.email} · Role <b>{state.user.role_code || "—"}</b> · {state.role_perms.length} role perms · {state.effective.length} effective
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={clearAll} disabled={busy}
                className="rounded-sm gap-1.5" data-testid="perm-clear-all">
                <RotateCcw className="w-3.5 h-3.5" /> Reset to role
              </Button>
              <Button size="sm" onClick={save} disabled={busy || !dirty}
                className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="perm-save">
                <Save className="w-3.5 h-3.5" /> Save {dirty && <span className="text-[10px]">({Object.keys(pending).length} changes)</span>}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto border rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-3 min-w-[180px]">Module</th>
                  {meta.actions.map(a => (
                    <th key={a} className="p-3 text-center">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meta.modules.map(mod => (
                  <tr key={mod.key} className="border-t hover:bg-slate-50" data-testid={`perm-row-${mod.key}`}>
                    <td className="p-3 font-medium">
                      {mod.label}
                      <div className="text-[10px] font-mono text-muted-foreground">{mod.key}</div>
                    </td>
                    {meta.actions.map(act => {
                      const s = cellStatus(mod.key, act);
                      return (
                        <td key={act} className="p-2 text-center">
                          <button onClick={() => cycleCell(mod.key, act)}
                            className={`w-12 h-9 rounded-sm border font-mono font-bold text-sm ${CELL_CLASS[s] || CELL_CLASS.inherit_off} hover:brightness-95 transition`}
                            data-testid={`perm-cell-${mod.key}-${act}`}
                            title={s === "allow" ? "Explicit Allow — click for Deny" :
                                     s === "deny"  ? "Explicit Deny — click to Reset" :
                                     s === "inherit_on"  ? "Inherited from role — click to Deny" :
                                                              "Not granted — click to Allow"}>
                            {CELL_ICON[s]}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground pt-2">
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm bg-slate-100 border border-slate-200 text-slate-600 font-mono text-[10px] text-center">✓</span> Inherit ON</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm bg-white border border-slate-200 text-slate-300 font-mono text-[10px] text-center">·</span> Inherit OFF</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm bg-emerald-500/15 border border-emerald-500 text-emerald-700 font-mono text-[10px] text-center">A</span> Explicit Allow</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm bg-red-500/15 border border-red-500 text-red-700 font-mono text-[10px] text-center">D</span> Explicit Deny</span>
          </div>
        </>
      )}
    </div>
  );
}
